import React, { useEffect, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import InputSelect from "../../components/InputSelect";
import Input from "../../components/Input";
import Button from "../../components/Button";
import Table from "../../components/Table";
import DotStatus from "../../components/DotStatus";

import api from "../../services/api";
import { createOptions } from "../../helpers/selects";
import { convertIntToMoney } from "../../helpers/conversions";
import { renderError } from "../../helpers/errors";
import { hasPermission } from "../../services/auth";
import { toast } from "react-toastify";

const tabs = [
    { key: 'all', label: 'Todas', status: null },
    { key: 'paid', label: 'Pagas', status: 'paid' },
    { key: 'pending', label: 'Pendentes', status: 'pending' }
];

const statusOptions = [
    { label: <span><DotStatus color="#4CAF50" size={15} /> Paga</span>, value: true },
    { label: <span><DotStatus color="#FFD600" size={15} /> Pendente</span>, value: false },
];

const noop = () => {};

const SelectStatusList = ({commission}) => {
    const [loading, setLoading] = useState(false);
    const [isPaid, setIsPaid] = useState(commission.is_paid);

    const updateComission = (newIsPaid) => {
        setLoading(true);

        api.put(`/order/commission/${commission.id}`, {
            admin_id: commission.admin_id,
            expected_payment_at: commission.expected_payment_at,
            is_paid: newIsPaid,
            paid_at: newIsPaid ? moment().format('YYYY-MM-DD') : null,
            order_id: commission.order_id,
            value: commission.value
        }).then(res => {
            setIsPaid(newIsPaid);
            toast.success('Status atualizado');
        }).catch(error => {
            renderError(error);
        }).then(() => {
            setLoading(false);
        });
    }

    return (
        <InputSelect
            loading={loading}
            options={statusOptions} 
            value={isPaid} 
            change={newIsPaid => updateComission(newIsPaid)}
            removeMargin={true}
        />
    );
}

const CommissionList = () => {
    const [admins, setAdmins] = useState([]);
    const [loadingAdmins, setLoadingAdmins] = useState(false);

    const [filters, setFilters] = useState({
        admin_id: null,
        order_code: '',
        start_date: moment().startOf('month').format('YYYY-MM-DD'),
        end_date: moment().endOf('month').format('YYYY-MM-DD')
    });

    const [activeTab, setActiveTab] = useState('all');
    const [commissions, setCommissions] = useState([]);
    const [summary, setSummary] = useState({ total: 0, paid: 0, pending: 0 });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAdmins();
    }, []);

    useEffect(() => {
        fetchCommissions();
    }, [activeTab]);

    const fetchAdmins = () => {
        setLoadingAdmins(true);
        api.get(`/list`).then(res => {
            setAdmins(res.data.resources ?? []);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoadingAdmins(false));
    };

    const getParams = () => {
        const params = {};

        if(filters.admin_id) params.admin_id = filters.admin_id;
        if(filters.order_code) params.order_code = filters.order_code;
        if(filters.start_date) params.start_date = filters.start_date;
        if(filters.end_date) params.end_date = filters.end_date;

        const currentTab = tabs.find(tab => tab.key === activeTab);
        if(currentTab && currentTab.status){
            params.status = currentTab.status;
        }

        return params;
    };

    const fetchCommissions = () => {
        setLoading(true);
        api.get(`/order/commission`, { params: getParams() }).then(res => {
            setCommissions(res.data.resources ?? []);
            setSummary(res.data.summary ?? { total: 0, paid: 0, pending: 0 });
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFilter = () => {
        fetchCommissions();
    };

    const adminOptions = createOptions(admins, 'name', 'id', true);

    const canEdit = hasPermission('edit_financial');

    const tableData = {
        header: [
            'Vendedor',
            'Pedido',
            'Data da emissão',
            'Valor do pedido',
            'Comissão',
            'Status',
            'Data do pagamento',
            'Comprovantes'
        ],
        rows: commissions.map(item => {
            const orderLink = item.order_id ? `/order/edit/${item.order_id}?tab=4` : '#';
            const receiptsCount = item.payment_receipts_count ?? 0;

            return {
                data: [
                    item.admin_name || '-',
                    item.order_code ? <Link to={orderLink}>{item.order_code}</Link> : '-',
                    item.order_created_at ? moment(item.order_created_at).format('DD/MM/YYYY') : '-',
                    `R$ ${convertIntToMoney(item.order_total || 0)}`,
                    `R$ ${convertIntToMoney(item.value || 0)}`,
                    canEdit ? 
                    <SelectStatusList commission={item} /> :
                    <span className="commission-status">
                        <DotStatus color={item.is_paid ? '#4CAF50' : '#FFD600'} size={15} />
                        {item.is_paid ? 'Paga' : 'Pendente'}
                    </span>,
                    item.paid_at ? moment(item.paid_at).format('DD/MM/YYYY') : '-',
                    receiptsCount > 0 ? <Link to={orderLink}>{`${receiptsCount} anexado${receiptsCount > 1 ? 's' : ''}`}</Link> : '-'
                ]
            };
        })
    };

    return (
        <PanelTemplate id="commission-list">
            <Box>
                <div className="commission-filters">
                    <div className="row">
                        {hasPermission('view_unrelated_order_commission') && 
                            <div className="col">
                                <InputSelect
                                    label="Vendedor"
                                    options={adminOptions}
                                    value={filters.admin_id}
                                    change={value => handleFilterChange('admin_id', value)}
                                    loading={loadingAdmins}
                                />
                            </div>
                        }
                        <div className="col">
                            <Input
                                label="Núm. do pedido"
                                value={filters.order_code}
                                change={value => handleFilterChange('order_code', value)}
                            />
                        </div>
                        <div className="col">
                            <Input
                                label="Pedidos emitidos a partir de"
                                type="date"
                                value={filters.start_date ?? ''}
                                change={value => handleFilterChange('start_date', value)}
                            />
                            <span className="input-help">Data da criação do pedido</span>
                        </div>
                        <div className="col">
                            <Input
                                label="Pedidos emitidos até"
                                type="date"
                                value={filters.end_date ?? ''}
                                change={value => handleFilterChange('end_date', value)}
                            />
                            <span className="input-help">Data da criação do pedido</span>
                        </div>
                        <div className="col" style={{ paddingTop: '5px' }}>
                            <Button
                                type="primary"
                                full={true}
                                text="Filtrar comissões"
                                action={handleFilter}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </Box>
            <Box>
                <div className="commission-tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            className={tab.key === activeTab ? 'active' : ''}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <Table header={tableData.header} rows={tableData.rows} loading={loading} />
            </Box>
            <Box>
                <div className="commission-summary row">
                    <div className="col-4">
                        <Input
                            label="Total de comissões"
                            value={`R$ ${convertIntToMoney(summary.total || 0)}`}
                            change={noop}
                            disabled={true}
                        />
                    </div>
                    <div className="col-4">
                        <Input
                            label="Comissões pagas"
                            value={`R$ ${convertIntToMoney(summary.paid || 0)}`}
                            change={noop}
                            disabled={true}
                        />
                    </div>
                    <div className="col-4">
                        <Input
                            label="Comissões pendentes"
                            value={`R$ ${convertIntToMoney(summary.pending || 0)}`}
                            change={noop}
                            disabled={true}
                        />
                    </div>
                </div>
            </Box>
        </PanelTemplate>
    );
};

export default CommissionList;

